// frontend/src/utils/deploySmoothing.js

// Default deploy duration (ms) used until we have a learned average.
const DEFAULT_DURATION_MS = 180000;
// Decay constant so the curve reaches ~90% at the estimated duration
// (1 - e^-2.3 ≈ 0.90).
const FINISH_K = 2.3;

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

// Turn DigitalOcean's coarse, jumpy step percentage into a continuously rising
// progress bar.
//
// DO only reports a handful of steps, so its raw percent snaps in big blocks
// (e.g. straight to 37%) and then sits flat for tens of seconds. The earlier
// approach showed `max(rawPercent, linearTimeEstimate)`, which let the raw
// value PIN the bar — so it reproduced exactly those snap-and-hang jumps.
//
// Instead we drive the bar purely by elapsed time against the average deploy
// duration, using an asymptotic "trickle" curve: it rises quickly early, slows
// as it nears the end, always keeps moving (never hangs), and never reaches
// 100 until the deploy is actually ACTIVE. The raw percent is used only to
// freeze the bar on a terminal failure and as a fallback when we have no start
// time to measure from.
export function computeSmoothedPercent({ realPercent = 0, phase, startedAt, estimatedDurationMs, now }) {
  const raw = clamp(Math.round(Number(realPercent) || 0), 0, 100);

  if (phase === 'ACTIVE') return 100;
  if (phase === 'ERROR' || phase === 'CANCELED' || phase === 'SUPERSEDED') return raw;

  // No start time → nothing to estimate from; show DO's raw value (held < 100).
  const startMs = startedAt ? new Date(startedAt).getTime() : null;
  if (!startMs) return Math.min(99, raw);

  const duration = estimatedDurationMs > 0 ? estimatedDurationMs : DEFAULT_DURATION_MS;
  const elapsed = Math.max(0, now - startMs);
  const progress = 100 * (1 - Math.exp((-FINISH_K * elapsed) / duration));
  return clamp(Math.round(progress), 0, 99);
}


// A deploy that is still in flight after this many multiples of the average
// duration is "stalled". The trickle bar pins at 99% by ~2x the average, so past
// that point the bar says nothing; the caller shows the real step and elapsed
// time instead. The floor keeps a fast learned average from flagging a merely
// slow deploy.
const STALL_FACTOR = 2.5;
const STALL_FLOOR_MS = 8 * 60 * 1000;
const TERMINAL_PHASES = ['ACTIVE', 'ERROR', 'CANCELED', 'SUPERSEDED'];

export function isDeployStalled({ phase, startedAt, estimatedDurationMs, now }) {
  if (!phase || TERMINAL_PHASES.includes(phase)) return false;
  const startMs = startedAt ? new Date(startedAt).getTime() : null;
  if (!startMs) return false;
  const duration = estimatedDurationMs > 0 ? estimatedDurationMs : DEFAULT_DURATION_MS;
  return now - startMs > Math.max(STALL_FACTOR * duration, STALL_FLOOR_MS);
}

// "19 min" style elapsed time for the stalled message.
export function formatElapsedMinutes(startedAt, now) {
  const startMs = startedAt ? new Date(startedAt).getTime() : null;
  if (!startMs) return '';
  return `${Math.max(0, Math.floor((now - startMs) / 60000))} min`;
}

export default computeSmoothedPercent;
